import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { designRequestsAPI, packagesAPI, clientsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import DesignRequestFormModal from './DesignRequestFormModal';
import { formatDate, handleApiError } from '../../utils/helpers';
import { PLATFORMS, CONTENT_TYPES } from '../../utils/platformConfig';
import { PlatformIcon } from '../icons/PlatformIcons';
import { Palette, Plus } from 'lucide-react';

const DesignRequestsPage = () => {
  const { user, isClient, isAdmin } = useAuth();
  const { isRTL, isArabic, t } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterClient]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (filterClient !== 'all') {
        params.client_id = filterClient;
      }

      const requestsResponse = await designRequestsAPI.getAll(params);
      setRequests(requestsResponse.data.requests);

      // Fetch clients for filter (admin only)
      if (isAdmin) {
        const clientsResponse = await clientsAPI.getAll();
        setClients(clientsResponse.data.clients);
      } else if (isClient) {
        // For client, get their client info
        const clientsResponse = await clientsAPI.getAll();
        if (clientsResponse.data.clients.length > 0) {
          setSelectedClient(clientsResponse.data.clients[0]);
        }
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = () => {
    // Both admin and client can create requests
    setShowFormModal(true);
  };

  const handleFormSuccess = () => {
    setShowFormModal(false);
    fetchData();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: isRTL ? 'قيد الانتظار' : 'Pending', color: 'bg-yellow-50 text-yellow-700 border border-yellow-100' },
      in_progress: { label: isRTL ? 'قيد التنفيذ' : 'In Progress', color: 'bg-blue-50 text-blue-700 border border-blue-100' },
      completed: { label: isRTL ? 'مكتمل' : 'Completed', color: 'bg-emerald-50 text-emerald-700 border border-emerald-100' },
      cancelled: { label: isRTL ? 'ملغي' : 'Cancelled', color: 'bg-rose-50 text-rose-700 border border-rose-100' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className={`text-center py-12 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchData} variant="secondary">
          {t('tryAgain')}
        </Button>
      </div>
    );
  }

  const statusCounts = {
    all: requests.length,
    pending: requests.filter((r) => r.status === 'pending').length,
    in_progress: requests.filter((r) => r.status === 'in_progress').length,
    completed: requests.filter((r) => r.status === 'completed').length,
    cancelled: requests.filter((r) => r.status === 'cancelled').length,
  };

  const filteredRequests = filterStatus === 'all'
    ? requests
    : requests.filter((r) => r.status === filterStatus);

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-5 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className="flex items-start justify-between gap-4">
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <div className="inline-flex items-center gap-2 mb-1">
              <Palette className="w-4 h-4 text-sky-600" />
              <span className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                {isRTL ? 'طلبات التصميم' : 'Design Requests'}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
              {t('designRequestsTitle')}
            </h1>
            <p className="text-slate-600 mt-1 text-sm">
              {isClient ? t('designRequestsSubtitleClient') : t('designRequestsSubtitleAdmin')}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-600">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/70 border border-slate-200">
                📋 {isRTL ? 'إجمالي الطلبات' : 'Total Requests'} {requests.length}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-50 border border-yellow-100 text-yellow-700">
                ⏳ {isRTL ? 'قيد الانتظار' : 'Pending'} {statusCounts.pending}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700">
                🔄 {isRTL ? 'قيد التنفيذ' : 'In Progress'} {statusCounts.in_progress}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700">
                ✅ {isRTL ? 'مكتملة' : 'Completed'} {statusCounts.completed}
              </span>
            </div>
          </div>
          <div className="flex items-start">
            <Button
              onClick={handleCreateRequest}
              variant="primary"
              className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
            >
              <Plus className={`w-4 h-4 ${isRTL ? 'mr-0 ml-2' : 'ml-0 mr-2'}`} />
              {t('newDesignRequest')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        {/* Status Filter Pills */}
        <div className={`flex flex-wrap gap-2 ${isRTL ? 'justify-end' : 'justify-start'}`}>
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
              filterStatus === 'all'
                ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
            }`}
          >
            {t('allRequestsFilter')} ({statusCounts.all})
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
              filterStatus === 'pending'
                ? 'bg-yellow-500 text-white border-yellow-500 shadow-sm'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
            }`}
          >
            {t('pendingRequestsFilter')} ({statusCounts.pending})
          </button>
          <button
            onClick={() => setFilterStatus('in_progress')}
            className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
              filterStatus === 'in_progress'
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
            }`}
          >
            {t('inProgressRequestsFilter')} ({statusCounts.in_progress})
          </button>
          <button
            onClick={() => setFilterStatus('completed')}
            className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
              filterStatus === 'completed'
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
            }`}
          >
            {t('completedRequestsFilter')} ({statusCounts.completed})
          </button>
        </div>

        {/* Client Filter (Admin only) */}
        {isAdmin && clients.length > 0 && (
          <select
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            className={`px-4 py-2 border border-slate-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${isRTL ? 'text-right' : 'text-left'}`}
          >
            <option value="all">{t('filterAllClients')}</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.company_name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <Card className="p-12">
          <div className={`text-center ${isRTL ? 'text-right' : 'text-left'}`}>
            <p className="text-gray-500 text-lg mb-4">
              {t('noDesignRequests')}
            </p>
            <Button variant="primary" onClick={handleCreateRequest}>
              {t('createYourFirstDesignRequest')}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRequests.map((request) => {
            const platformInfo = request.platform ? PLATFORMS[request.platform] : null;
            const contentTypeInfo = request.content_type ? CONTENT_TYPES[request.content_type] : null;

            return (
              <Card
                key={request.id}
                className="relative flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Status Badge */}
                <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'}`}>
                  {getStatusBadge(request.status)}
                </div>

                {/* Header */}
                <div className={`mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <h3 className="text-base font-semibold text-slate-900 mb-2 pr-12">
                    {request.client_name}
                  </h3>
                  {platformInfo && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center justify-center w-7 h-7 rounded-full bg-slate-100">
                        <PlatformIcon platformId={platformInfo.id} className="w-4 h-4" />
                      </div>
                      {contentTypeInfo && (
                        <span className="text-xs text-slate-500">
                          {contentTypeInfo.icon} {isArabic ? contentTypeInfo.nameAr : contentTypeInfo.name}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Description */}
                {request.description && (
                  <p className={`text-xs text-slate-500 line-clamp-3 mb-4 flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {request.description}
                  </p>
                )}

                {/* Assigned Users */}
                {(request.designer_name || request.writer_name) && (
                  <div className="flex items-center gap-2 text-xs mb-4">
                    {request.designer_name && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                        🎨 {request.designer_name}
                      </span>
                    )}
                    {request.writer_name && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                        ✍️ {request.writer_name}
                      </span>
                    )}
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100 mt-auto mb-3">
                  <span>{formatDate(request.created_at)}</span>
                  {request.created_by_name && (
                    <span className="text-slate-400">{isRTL ? `بواسطة ${request.created_by_name}` : `by ${request.created_by_name}`}</span>
                  )}
                </div>

                {/* Actions */}
                <div className="pt-3 border-t border-slate-200">
                  <Link to={`/design-requests/${request.id}`} className="block">
                    <Button variant="secondary" size="sm" className="w-full">
                      {t('viewDetails')}
                    </Button>
                  </Link>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <DesignRequestFormModal
          clientId={isClient && selectedClient ? selectedClient.id : null}
          onClose={() => setShowFormModal(false)}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default DesignRequestsPage;





